import React, { useEffect, useState, useRef, useCallback } from 'react';
import PendingProjectDetailModal from './project/modals/PendingProjectDetailModal';
import { readPendingWarm, clearPendingCache, warmPending } from '../../utils/warmPendingCache';
import { clearApplyCache, warmApply } from '../../utils/warmApplyCache';
import '../../styles/common/ib-layout.css';
import '../../styles/common/ib-forms.css';

// LRU 캐시 헬퍼
function setCacheWithLimit(map, key, value, maxSize = 50) {
  if (map.has(key)) {
    map.delete(key);
  }
  if (map.size >= maxSize) {
    const firstKey = map.keys().next().value;
    map.delete(firstKey);
  }
  map.set(key, value);
}

export default function InfluencerProjectApplyCheck() {
  const uid = window.__ME__?.id || 'me';
  const initialWarm = readPendingWarm(uid);
  
  const [items, setItems] = useState(initialWarm?.items || []);
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState(null);

  const detailViewCache = useRef(new Map());
  const cardRefs = useRef(new Map());
  const observerRef = useRef(null);
  const itemsRef = useRef(items);

  useEffect(() => { itemsRef.current = items; }, [items]);

  useEffect(() => {
    const handleUpdate = () => {
      const warm = readPendingWarm(uid);
      if (warm?.items) {
        setItems(warm.items);
        warm.items.forEach(app => {
          if (app?.id && app?.project) {
            setCacheWithLimit(detailViewCache.current, app.id, { ...app }, 50);
          }
        });
      }
    };
  
    window.addEventListener('pending-list-update', handleUpdate);
    return () => window.removeEventListener('pending-list-update', handleUpdate);
  }, [uid]);

  useEffect(() => {
    const warm = readPendingWarm(uid);
    if (warm?.items) {
      setItems(warm.items);
      warm.items.forEach(app => {
        if (app?.id && app?.project) {
          setCacheWithLimit(detailViewCache.current, app.id, { ...app }, 50);
        }
      });
    }
  }, [uid]);

  const prepareCardDetail = useCallback(async (app) => {
    if (!app || detailViewCache.current.has(app.id)) return;
    
    const urls = [app.project?.cover].concat(app.project?.gallery || []).filter(Boolean);
    
    await Promise.all(urls.map(u => new Promise(res => {
      try {
        const img = new Image();
        img.onload = img.onerror = () => res();
        img.src = u;
        if (img.decode) img.decode().then(res).catch(res);
      } catch { res(); }
    })));
    
    setCacheWithLimit(detailViewCache.current, app.id, { ...app }, 50);
  }, []);

  useEffect(() => {
    if (!('IntersectionObserver' in window)) return;
    
    const currentCardRefs = cardRefs.current;
    
    observerRef.current = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          const appId = entry.target.dataset.appId;
          const application = itemsRef.current.find(app => app?.id === appId);
          if (application && !detailViewCache.current.has(appId)) {
            prepareCardDetail(application);
          }
        }
      });
    }, { rootMargin: '50px' });

    return () => {
      if (observerRef.current) {
        observerRef.current.disconnect();
        observerRef.current = null;
      }
      currentCardRefs.clear();
    };
  }, [prepareCardDetail]);

  const registerCard = (el, appId) => {
    if (!el || !appId) return;
    const prevEl = cardRefs.current.get(appId);
    if (prevEl && prevEl !== el) {
      observerRef.current?.unobserve(prevEl);
    }
    cardRefs.current.set(appId, el);
    observerRef.current?.observe(el);
  };

  const handleCancelSuccess = async () => {
    if (!uid) return;
    
    const canceledProjectId = detail?.projectId || detail?.project?.id;
    
    setOpen(false);
    if (canceledProjectId) {
      setItems(prevItems => prevItems.filter(app => app?.project?.id !== canceledProjectId));
    }
    
    clearPendingCache(uid);
    clearApplyCache(uid);
    
    (async () => {
      try {
        await Promise.all([
          warmPending({ uid, awaitReady: 'covers' }),
          warmApply({ uid, sort: 'deadline', page: 1, awaitReady: 'covers' })
        ]);
        
        const newWarm = readPendingWarm(uid);
        if (newWarm?.items) {
          setItems(newWarm.items);
          newWarm.items.forEach(app => {
            if (app?.id && app?.project) {
              setCacheWithLimit(detailViewCache.current, app.id, { ...app }, 50);
            }
          });
        } else {
          setItems([]);
        }
        
        window.dispatchEvent(new CustomEvent('project-list-update'));
        
      } catch (err) {
        console.error('백그라운드 동기화 실패:', err);
        const warm = readPendingWarm(uid);
        if (warm?.items) setItems(warm.items);
      }
    })();
  };

  const fmt = (n) => {
    const v = typeof n === 'number' ? n : Number(n);
    return Number.isFinite(v) ? v.toLocaleString('ko-KR') : (n ?? '');
  };

  const getServiceTypeLabel = (type) => {
    const labels = {
      'delivery': '제품 제공',
      'purchase': '제품 미제공',
      'visit': '장소 광고',
      'free': '광고주 자율방식',
      'custom': '광고주 자율방식'
    };
    return labels[type] || type;
  };

  const getDeadlineText = (recruitEnd) => {
    if (!recruitEnd) return null;
    const end = new Date(recruitEnd);
    const now = new Date();
    const diffMs = end - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return '모집 마감';
    if (diffDays === 0) return '오늘 모집 마감!';
    if (diffDays === 1) return '모집 마감 1일 전';
    if (diffDays <= 3) return `모집 마감 ${diffDays}일 전`;
    return null;
  };

  const getDeadlineUrgency = (recruitEnd) => {
    if (!recruitEnd) return 'normal';
    const end = new Date(recruitEnd);
    const now = new Date();
    const diffMs = end - now;
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays <= 0) return 'urgent';
    if (diffDays === 1) return 'urgent';
    if (diffDays <= 3) return 'warning';
    return 'normal';
  };

  const shortDate = (d) => {
    if (!d) return '';
    const x = new Date(d);
    return `${x.getMonth() + 1}/${x.getDate()}`;
  };

  return (
    <div>
      <div className="mb-page-header" style={{ display: 'flex', alignItems: 'flex-start', gap: 16, flexWrap: 'wrap' }}>
        <div style={{ flex: 1, minWidth: 220 }}>
          <h2 className="mb-page-title">지원 중인 프로젝트</h2>
          <p className="mb-page-description">지원은 동시에 최대 10건까지 유지할 수 있어요.</p>
        </div>
        <span className="badge badge--blue">{items.length} / 10건 심사 중</span>
      </div>

      {items.length === 0 ? (
        <div className="mb-card" style={{ padding: '72px 24px', textAlign: 'center' }}>
          <div style={{ fontSize: 17, fontWeight: 800, color: '#3b4658', marginBottom: 6 }}>
            지원 중인 프로젝트가 없어요
          </div>
          <div style={{ fontSize: 14, color: '#6b7686' }}>
            프로젝트 지원하기에서 새로운 프로젝트에 지원해보세요
          </div>
        </div>
      ) : (
        <div className="mbh-plist">
          {items.map((app) => {
            const deadlineText = getDeadlineText(app.project?.recruitEnd);
            const urgency = getDeadlineUrgency(app.project?.recruitEnd);
            return (
              <button
                key={app.id}
                type="button"
                ref={(el) => app?.id && registerCard(el, app.id)}
                data-app-id={app.id}
                className="mbh-pcard"
                onMouseEnter={() => { if (app) prepareCardDetail(app); }}
                onClick={() => {
                  if (!app) return;
                  const cachedView = detailViewCache.current.get(app.id);
                  setDetail(cachedView || app);
                  setOpen(true);
                }}
              >
                <span className="mbh-pcard__logo" aria-hidden="true">
                  {app.project?.cover
                    ? <img src={app.project.cover} alt="" loading="eager" decoding="async" />
                    : (app.project?.productName || '프').charAt(0)}
                </span>
                <span className="mbh-pcard__meta">
                  <span className="mbh-pcard__name">{app.project?.productName || '광고 프로젝트'}</span>
                  <span className="mbh-pcard__sub">
                    {[
                      app.appliedAt ? `${shortDate(app.appliedAt)} 지원` : null,
                      getServiceTypeLabel(app.project?.serviceType),
                      `보상 ${fmt(app.project?.rewardPointPerCreator || 0)}P`,
                      app.project?.perPurchaseReward?.use ? `구매당 +${fmt(app.project.perPurchaseReward.rewardPerBuyer)}P` : null,
                    ].filter(Boolean).join(' · ')}
                  </span>
                </span>
                <span className="mbh-pcard__stat" style={{ minWidth: 0 }}>
                  <span className={`badge ${urgency === 'urgent' ? 'badge--red' : 'badge--blue'}`}>
                    {deadlineText
                      ? `심사 중 · ${deadlineText}`
                      : `심사 중${app.project?.recruitEnd ? ` · 발표 ${shortDate(app.project.recruitEnd)}` : ''}`}
                  </span>
                </span>
              </button>
            );
          })}
        </div>
      )}

      <div className="note note--blue">
        선정되면 "선정된 프로젝트 계약" 화면과 알림으로 알려드려요. 응답 기한 안에 계약을 수락해야 확정돼요.
        지원 취소는 카드를 눌러 상세에서 할 수 있어요.
      </div>

      <PendingProjectDetailModal
        open={open}
        onClose={() => setOpen(false)}
        application={detail}
        onCancelSuccess={handleCancelSuccess}
      />
    </div>
  );
}
